import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import AppWrapper from '../../../components/AppWrapper';
import EditFooterBar from '../../../components/EditFooterBar';
import ErrorMessage from '../../../components/ErrorMessage';
import { FileInput } from '../../../components/FileInput';
import FormLabelText from '../../../components/FormLabelText';
import InputCheckbox from '../../../components/InputCheckbox/Index';
import InputNumber from '../../../components/InputNumber';
import InputSelect from '../../../components/InputSelect';
import InputText from '../../../components/InputText';
import { TextArea } from '../../../components/InputTextArea';
import PageTitle from '../../../components/PageTitle';
import { TextP } from '../../../components/Text';
import { useStudyResult } from '../../../hooks';
import * as Styled from '../../../styles/EditDeleteStyles';
import { colours, fonts } from '../../../styles/theme';
import { breachcrumbPath } from '../../../utils/breadcrumbConst';
import { resultStatusOptions } from '../../../utils/const';
import { LinkStyled } from '../../../components/Link/styles';
import { LabelStyled } from '../../../components/InputText/styles';

const StudyResultEdit = () => {
  const params = useParams();
  const location = useLocation();

  const {
    baseUrl,
    nameSingle,
    saveName,
    pageType,
    studyResult,
    toastMessage,
    resourceFile,
    projects,
    handleChange,
    getStudyResult,
    handleSubmitButton,
    setPageType,
    setStudyResult,
    setResourceFile,
    errors,
    setResourceFileDelect,
    resourceFileDelect,
  } = useStudyResult();

  const titleOptions: { [key: string]: string } = {
    change: `Change ${nameSingle}`,
    add: `Add ${nameSingle}`,
  };

  useEffect(() => {
    if (params.id) {
      getStudyResult(parseInt(params.id, 10));
    }
  }, [params]);

  useEffect(() => {
    if (location?.pathname.includes('change')) {
      setPageType('change');
    }
    if (location?.pathname.includes('add')) {
      setPageType('add');
    }
    if (!params?.id) {
      setStudyResult({});
    }
  }, [location]);

  return (
    <AppWrapper
      toastMessage={toastMessage}
      breadcrumbs={[
        breachcrumbPath.core,
        breachcrumbPath.studyResult,
        { url: '', name: `StudyResult object (${studyResult?.id})` },
      ]}
    >
      <PageTitle
        title={titleOptions[pageType]}
        linkUrl={`/${baseUrl}/${studyResult?.id}/history`}
        buttonText="history"
        showButton={pageType === 'change'}
      />
      <TextP color={colours.colour1} font={fonts.font1.H16B}>
        Study Result object ({saveName})
      </TextP>
      {Object.keys(errors).length > 0 && <ErrorMessage message={errors.fetch} />}
      <Styled.FormWrapper>
        <Styled.InputWrapper>
          <InputCheckbox
            label="completed"
            checkedValue={studyResult?.completed || false}
            handleChange={(value) => handleChange(value, 'completed')}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <TextArea
            label="summary:"
            textValue={studyResult?.summary || ''}
            handleHandle={(value) => handleChange(value, 'summary')}
            keyName="summary"
            error={errors.summary}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputNumber
            id="patient_partners_count"
            label="patient partners count:"
            inputValue={studyResult?.patient_partners_count}
            handleHandle={handleChange}
            keyName="patient_partners_count"
            padding={false}
            error={errors.patient_partners_count}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <TextArea
            label="patient partners engagement strategy:"
            textValue={studyResult?.patient_partners_engagement_strategy || ''}
            handleHandle={(value) => handleChange(value, 'patient_partners_engagement_strategy')}
            keyName="patient_partners_engagement_strategy"
            error={errors.patient_partners_engagement_strategy}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <TextArea
            label="study limitations:"
            textValue={studyResult?.study_limitations || ''}
            handleHandle={(value) => handleChange(value, 'study_limitations')}
            keyName="study_limitations"
            error={errors.study_limitations}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputText
            id="resource_website_name"
            label="resource website name:"
            inputValue={`${studyResult?.resource_website_name}` || ''}
            handleHandle={handleChange}
            keyName="resource_website_name"
            padding={false}
            theme="form"
            error={errors.resource_website_name}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputText
            id="resource_website_url"
            label="resource website url:"
            placeholder="https://www.example.com"
            inputValue={`${studyResult?.resource_website_url}` || ''}
            handleHandle={handleChange}
            keyName="resource_website_url"
            padding={false}
            theme="form"
            error={errors.resource_website_url}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputText
            id="resource_file_name"
            label="resource file name:"
            inputValue={`${studyResult?.resource_file_name}` || ''}
            handleHandle={handleChange}
            keyName="resource_file_name"
            padding={false}
            theme="form"
            error={errors.resource_file_name}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <FileInput
            label="resource Filename:"
            handleChange={(value) => setResourceFile(value)}
            field="resourceFile"
            fileLink={studyResult?.fileLink}
            fileName={resourceFile?.name || `${studyResult?.resource_file}`}
            isChecked={resourceFileDelect}
            handleCheck={(value: boolean) => {
              setResourceFileDelect(value);
            }}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <TextArea
            label="comments:"
            textValue={studyResult?.comments || ''}
            handleHandle={(value) => handleChange(value, 'comments')}
            keyName="comments"
            error={errors.comments}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <TextArea
            label="admin comments:"
            textValue={studyResult?.admin_comments || ''}
            handleHandle={(value) => handleChange(value, 'admin_comments')}
            keyName="admin_comments"
            error={errors.admin_comments}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputSelect
            label="status:"
            onChange={(value) => handleChange(value, 'status')}
            options={resultStatusOptions}
            selectedValue={studyResult?.status || ''}
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <FormLabelText label="Result Created by:" textValue={studyResult?.created_by || '-'} />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <FormLabelText label="Last Updated by:" textValue={studyResult?.updated_by_user || '-'} />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputSelect
            label="Project:"
            onChange={(value) => handleChange(value, 'status')}
            options={projects}
            selectedValue={studyResult?.project_id || ''}
            width="80%"
          />
        </Styled.InputWrapper>

        <Styled.InputWrapper>
          <InputCheckbox
            label="Admin to Review"
            checkedValue={studyResult?.admin_to_review || false}
            handleChange={(value) => handleChange(value, 'admin_to_review')}
          />
        </Styled.InputWrapper>
        <Styled.InputWrapper>
          <InputCheckbox
            label="Share on HMC"
            checkedValue={studyResult?.share_on_hmc || false}
            handleChange={(value) => handleChange(value, 'share_on_hmc')}
          />
        </Styled.InputWrapper>
      </Styled.FormWrapper>
      {studyResult?.status === 'approved' && studyResult?.share_on_hmc && (
        <>
          <Styled.InputWrapper>
            <InputText
              id="hmc_identifier"
              label="HMC Identifier:"
              inputValue={studyResult?.hmc_identifier ?? ''}
              handleHandle={handleChange}
              keyName="hmc_identifier"
              padding={false}
              theme="form"
              error={errors.hmc_identifier}
            />
          </Styled.InputWrapper>
          <Styled.InputWrapper>
            <InputText
              id="hmc_page_url"
              label="HMC Page URL:"
              inputValue={studyResult?.hmc_page_url ?? ''}
              handleHandle={handleChange}
              keyName="hmc_page_url"
              padding={false}
              theme="form"
              error={errors.hmc_page_url}
            />
          </Styled.InputWrapper>
          <div style={{ paddingLeft: '10px', paddingTop: '10px' }}>
            <LabelStyled>Reach study result URL:</LabelStyled>
            <LinkStyled
              href={`${process.env.REACT_APP_WEBSITE_URL}/project/${studyResult.project_id}/results`}
            >
              {`${process.env.REACT_APP_WEBSITE_URL}/project/${studyResult.project_id}/results`}
            </LinkStyled>
          </div>
        </>
      )}
      <EditFooterBar
        handleSubmit={handleSubmitButton}
        urlPath={`${baseUrl}/${studyResult?.id}`}
        showNew={false}
        showDelete
      />
    </AppWrapper>
  );
};

export default StudyResultEdit;
